import React, { useState } from 'react';
import { FormControl, InputLabel, Select, InputAdornment, TextField } from '@mui/material';
import { UserType } from '../../../common/commonTypes';
import Alert from '@mui/material/Alert';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import LoadingButton from '@mui/lab/LoadingButton';
import { SelectChangeEvent } from '@mui/material';
import MenuItem from '@mui/material/MenuItem';
import { Trans, useTranslation } from 'react-i18next';
import { supportedLanguages } from '../../../common/supportedLanguages';
import { saveSettings } from '../../../api';
import Divider from '@mui/material/Divider';
import { macrosChartTypes } from '../../../common/macrosChartTypes';

type ModalSettingsType = {
    user: UserType;
    onCancel: () => void;
    onSuccess: () => void;
};

const SettingsModal = (props: ModalSettingsType) => {
    const { user, onCancel, onSuccess } = props;

    const { t, i18n } = useTranslation();

    const [loading, setLoading] = useState<boolean>(false);
    const [errorMessage, setErrorMessage] = useState<string>('');
    const [selectedLanguage, setSelectedLanguage] = useState<string>(i18n.language);
    const [macrosChartType, setMacrosChartType] = useState<string>(user.settings.macrosChartType);
    const [goals, setGoals] = useState<any>(user.settings.goals);
    const [goalsInputsDisabled, setGoalsInputsDisabled] = useState<boolean>(
        user.settings.macrosChartType !== 'GOALS'
    );

    const handleLanguageChange = (event: SelectChangeEvent<string>) => {
        const newLanguage = event.target.value;
        setSelectedLanguage(newLanguage);
    };

    const handleMacrosChartTypeChange = (event: SelectChangeEvent<string>) => {
        const newType = event.target.value;
        setMacrosChartType(newType);
        setGoalsInputsDisabled(newType !== 'GOALS');
    };

    const handleGoalsChange = (event: any, key: string) => {
        const { value } = event.target;
        const prevGoals = { ...goals };
        const goalsToSet = { ...prevGoals, [key]: Number(value) };
        setGoals(goalsToSet);
    };

    const handleSave = async () => {
        setLoading(true);
        setErrorMessage('');

        try {
            // update on BE
            await saveSettings(user.id, {
                languageCode: selectedLanguage,
                goals,
                // @ts-ignore
                macrosChartType,
            });

            // update in place
            user.settings = {
                ...user.settings,
                languageCode: selectedLanguage,
                goals,
                // @ts-ignore
                macrosChartType,
            };

            window.gtag?.('event', `Saved Settings`, {});

            setLoading(false);
            i18n.changeLanguage(selectedLanguage);
            onSuccess();
        } catch (error: any) {
            console.log('%c Error saving settings >>> ', 'background: #333; color: #bada55', error);

            const message =
                error.response.data.message ||
                error.response.data.error ||
                `Can't save settings, please try again later`;
            setErrorMessage(message);
            setLoading(false);
        }
    };

    return (
        <Dialog
            fullWidth
            open
            onClose={(event, reason) => {
                if (reason !== 'backdropClick') {
                    onCancel();
                }
            }}
        >
            <DialogTitle>{t('DRAWER_BTN_settings')}</DialogTitle>
            <DialogContent>
                {Boolean(errorMessage) && (
                    <Alert severity="error" className="mb-4">
                        {errorMessage}
                    </Alert>
                )}

                {/* CHARTS TYPE */}
                <FormControl sx={{ m: 1, minWidth: 180 }} size="small" variant="standard">
                    <InputLabel id="chart-type-selector-label">{t('macrosChartType')}</InputLabel>
                    <Select
                        labelId="chart-type-selector-label"
                        value={macrosChartType}
                        onChange={handleMacrosChartTypeChange}
                    >
                        {macrosChartTypes.map((key) => (
                            <MenuItem key={key} value={key}>
                                {t(`macrosChartType_${key}`)}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>

                <div className="ml-4 mb-2 text-xs text-gray-600 text-right">
                    <Trans i18nKey={`macrosChartType_${macrosChartType}_INFO`} />
                </div>

                <Divider flexItem className="!my-4 text-sm ">
                    {t('edit_my_goals')}
                </Divider>

                {/* GOALS*/}
                <div className="mt-4 mb-8">
                    <div className="mb-4 flex justify-between">
                        <TextField
                            style={{ width: '48%' }}
                            label={t('calories')}
                            value={String(goals.calories)}
                            onChange={(e) => {
                                handleGoalsChange(e, 'calories');
                            }}
                            type="number"
                            fullWidth
                            size="small"
                            slotProps={{
                                htmlInput: { min: 0, max: 5000 },
                            }}
                            disabled={goalsInputsDisabled}
                        />
                    </div>
                    <div className="mb-4 flex justify-between">
                        <TextField
                            style={{ width: '48%' }}
                            label={t('protein')}
                            value={String(goals.protein)}
                            onChange={(e) => {
                                handleGoalsChange(e, 'protein');
                            }}
                            type="number"
                            fullWidth
                            size="small"
                            slotProps={{
                                input: {
                                    endAdornment: (
                                        <InputAdornment position="end">{t('gr')}</InputAdornment>
                                    ),
                                },
                                htmlInput: { min: 0, max: 500 },
                            }}
                            disabled={goalsInputsDisabled}
                        />
                        <TextField
                            style={{ width: '48%' }}
                            label={t('fat')}
                            value={String(goals.fats)}
                            onChange={(e) => {
                                handleGoalsChange(e, 'fats');
                            }}
                            type="number"
                            fullWidth
                            size="small"
                            slotProps={{
                                input: {
                                    endAdornment: (
                                        <InputAdornment position="end">{t('gr')}</InputAdornment>
                                    ),
                                },
                                htmlInput: { min: 0, max: 500 },
                            }}
                            disabled={goalsInputsDisabled}
                        />
                    </div>
                    <div className="mb-4 flex justify-between">
                        <TextField
                            style={{ width: '48%' }}
                            label={t('carbs_excl_sugar')}
                            value={String(goals.carbsNoSugars)}
                            onChange={(e) => {
                                handleGoalsChange(e, 'carbsNoSugars');
                            }}
                            type="number"
                            fullWidth
                            size="small"
                            slotProps={{
                                input: {
                                    endAdornment: (
                                        <InputAdornment position="end">{t('gr')}</InputAdornment>
                                    ),
                                },
                                htmlInput: { min: 0, max: 500 },
                            }}
                            disabled={goalsInputsDisabled}
                        />
                        <TextField
                            style={{ width: '48%' }}
                            label={t('sugar')}
                            value={String(goals.sugars)}
                            onChange={(e) => {
                                handleGoalsChange(e, 'sugars');
                            }}
                            type="number"
                            fullWidth
                            size="small"
                            slotProps={{
                                input: {
                                    endAdornment: (
                                        <InputAdornment position="end">{t('gr')}</InputAdornment>
                                    ),
                                },
                                htmlInput: { min: 0, max: 500 },
                            }}
                            disabled={goalsInputsDisabled}
                        />
                    </div>
                </div>

                <Divider flexItem className="!mt-10 !mb-4 text-sm text-gray-700" />

                {/* LANG*/}
                <FormControl sx={{ m: 1, minWidth: 180 }} size="small" variant="standard">
                    <InputLabel id="language-selector-label">{t('language')}</InputLabel>
                    <Select
                        labelId="language-selector-label"
                        value={selectedLanguage}
                        onChange={handleLanguageChange}
                    >
                        {Object.entries(supportedLanguages).map(([key, label]) => (
                            <MenuItem key={key} value={key}>
                                {label}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
            </DialogContent>

            <DialogActions>
                <LoadingButton onClick={onCancel} disabled={loading}>
                    {t('MODAL_cancel')}
                </LoadingButton>
                <LoadingButton
                    onClick={handleSave}
                    loading={loading}
                    variant="contained"
                    color="success"
                >
                    {t('MODAL_save')}
                </LoadingButton>
            </DialogActions>
        </Dialog>
    );
};

export default SettingsModal;
